import React, { useEffect, useMemo, useState } from "react";
import Modal from "../ui/Modal";
import SearchableSelect from "../ui/SearchableSelect";
import { useScheduleData } from "./ScheduleDataContext";
import { getProjectIdFromTicket, getTicketSummary, postScheduledResource, postAddMemberToProject, fetchTicketsByProjectIds } from "../../api/client";
import { apiConfig, buildApiUrl } from "../../apiConfig";

function unwrapId(v) { if (v==null) return null; if (typeof v==='object') return unwrapId(v.id ?? v.identifier ?? v.value ?? v.objectId ?? v.ticketId ?? v.projectId ?? null); return v; }
function extractProjectId(p) { return unwrapId(p?.id) ?? unwrapId(p?.projectId) ?? unwrapId(p?.project) ?? unwrapId(p?.['project/id']) ?? null; }
function extractProjectName(p) { return p?.name || p?.project?.name || `Project ${extractProjectId(p)}`; }
function extractTicketId(t) {
  const v = t?.id ?? t?.ticketId ?? t?.objectId ?? null;
  if (v && typeof v === 'object') {
    return v.id ?? v.identifier ?? v.value ?? null;
  }
  return v;
}
function toUtcStartOfDayIso(dateStr) {
  if (!dateStr) return null;
  // Parse YYYY-MM-DD as literal calendar date (no timezone influence)
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr));
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const utc = new Date(Date.UTC(y, mo, d, 0, 0, 0));
  const iso = utc.toISOString();
  return iso.slice(0, 19) + 'Z';
}

export default function NewScheduledResourceModalFixed({ open, onClose }) {
  const { token, projects, tickets, ticketProjectMap, setAssignments, setTickets, projectRoles, backgroundLoading } = useScheduleData();
  const [searchProject, setSearchProject] = useState("");
  // Ticket search handled inside SearchableSelect
  const [searchUser, setSearchUser] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [manualTicketId, setManualTicketId] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [rolePickerVisible, setRolePickerVisible] = useState(false);
  const [selectedProjectRoleId, setSelectedProjectRoleId] = useState(null);
  const [addingToProject, setAddingToProject] = useState(false);
  const [projectTickets, setProjectTickets] = useState([]);
  const [loadingProjectTickets, setLoadingProjectTickets] = useState(false);
  const [projectTicketsError, setProjectTicketsError] = useState(null);

  function isMemberNotAssignedErr(err) {
    if (!err) return false;
    const msg = String(err.message || err.body || '').toLowerCase();
    return msg.includes('member not assigned to project');
  }

  useEffect(() => {
    if (!open) return; let cancelled = false;
    (async () => {
      try {
        setLoadingUsers(true);
        const url = buildApiUrl(apiConfig.cwUsersPath, { conditions: "inactiveFlag=false" });
        const res = await fetch(url, { headers: { Accept: 'application/json', ...(token ? { Authorization: token } : {}) } });
        const data = res.ok ? await res.json() : [];
        const list = Array.isArray(data) ? data : (Array.isArray(data.items) ? data.items : []);
        if (!cancelled) setUsers(list);
      } catch(e){
        console.error(e);
        if (!cancelled) setUsers([]);
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, token]);

  // Fetch tickets by selected project id directly from API so the modal works even
  // when tickets are not preloaded in the global cache.
  useEffect(() => {
    if (!open) return;
    if (!selectedProjectId) { setProjectTickets([]); setProjectTicketsError(null); return; }
    let cancelled = false;
    (async () => {
      try {
        setProjectTicketsError(null);
        setLoadingProjectTickets(true);
        const list = await fetchTicketsByProjectIds([selectedProjectId], token, { pageSize: 200 });
        if (!cancelled) setProjectTickets(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) {
          setProjectTickets([]);
          setProjectTicketsError(e);
        }
      } finally {
        if (!cancelled) setLoadingProjectTickets(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, selectedProjectId, token]);

  // When modal opens, if dates are blank initialize to a past->present sensible default
  useEffect(() => {
    if (!open) return;
    if (!dateStart && !dateEnd) {
      const today = new Date();
      const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
      const pad = (n) => String(n).padStart(2, '0');
      const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      setDateStart(fmt(yesterday));
      setDateEnd(fmt(today));
    }
  }, [open, dateStart, dateEnd]);

  const filteredProjects = useMemo(() => { const n = searchProject.trim().toLowerCase(); const arr = projects||[]; if (!n) return arr; return arr.filter(p => { const id = String(extractProjectId(p)??""); const nm = (extractProjectName(p)||'').toLowerCase(); return id.includes(n)||nm.includes(n); }); }, [projects, searchProject]);

  function applyProjectSearch() {
    const raw = searchProject.trim();
    if (!raw) return;
    // Try to match an exact project id from the filtered list
    const exact = filteredProjects.find(p => String(extractProjectId(p)) === raw);
    let idToUse = null;
    if (exact) {
      idToUse = extractProjectId(exact);
    } else if (filteredProjects.length > 0) {
      // Fallback: pick the first filtered project
      idToUse = extractProjectId(filteredProjects[0]);
    } else if (/^\d+$/.test(raw)) {
      // As a last resort, if the input looks like a numeric ID, use it directly
      idToUse = raw;
    }
    if (!idToUse) return;
    setSelectedProjectId(idToUse);
    setSelectedTicketId(null);
    setManualTicketId("");
  }
  const ticketsForProject = useMemo(() => {
    if (!selectedProjectId) return [];
    const all = tickets || [];
    const scoped = all.filter((t) => {
      const tid = extractTicketId(t);
      const pidFromMap = tid != null ? ticketProjectMap.get(tid) : null;
      if (pidFromMap != null) return String(pidFromMap) === String(selectedProjectId);
      // Fallback: derive directly if map missing
      const pid = getProjectIdFromTicket(t);
      return pid != null && String(pid) === String(selectedProjectId);
    });
    if (!scoped.length) {
      console.warn('[Modal] No tickets matched selected project', selectedProjectId, 'Total tickets:', all.length);
    }
    // Merge with tickets fetched directly for this project, deduping by ticket id
    const remote = projectTickets || [];
    const seen = new Set();
    const merged = [];
    const pushIfNew = (t) => {
      const id = extractTicketId(t);
      const key = id != null ? String(id) : JSON.stringify(t);
      if (!seen.has(key)) { seen.add(key); merged.push(t); }
    };
    scoped.forEach(pushIfNew);
    remote.forEach(pushIfNew);
    return merged;
  }, [tickets, ticketProjectMap, selectedProjectId, projectTickets]);
  const filteredUsers = useMemo(() => { const n = searchUser.trim().toLowerCase(); const arr = users||[]; if (!n) return arr; return arr.filter(u => { const id = String(u?.id??""); const nm = (u?.name||u?.fullName||u?.identifier||'').toLowerCase(); return id.includes(n)||nm.includes(n); }); }, [users, searchUser]);

  async function onSubmit(){
    try {
      setError(null);
      if(!selectedProjectId) throw new Error("Select a project");
      if(!selectedTicketId) throw new Error("Select a ticket");
      if(!selectedUser) throw new Error("Select a user");
      if(!dateStart||!dateEnd) throw new Error("Select a date range");
      const ds = toUtcStartOfDayIso(dateStart);
      const de = toUtcStartOfDayIso(dateEnd);
      if(!ds||!de) throw new Error("Invalid date format");
      if(new Date(de) < new Date(ds)) throw new Error("End date must be after start date");
      const member = (selectedUser?.id!=null)?{id:selectedUser.id}:(selectedUser?.identifier?{identifier:String(selectedUser.identifier)}:null);
      if(!member) throw new Error("Selected user missing id/identifier");
      const ticketIdForPayload = (() => {
        const raw = selectedTicketId;
        const s = String(raw ?? '').trim();
        if (/^\d+$/.test(s)) return Number(s);
        return raw;
      })();
      const payload = { objectId: ticketIdForPayload, member, dateStart: ds, dateEnd: de, type:{identifier:"S"}, span:{identifier:"N"} };
      setSubmitting(true);
      const created = await postScheduledResource(payload, token);
      const ticketId = ticketIdForPayload;
      const projectId = ticketProjectMap.get(ticketId) || selectedProjectId || null;
      // Adjust UTC midnight to local midnight so the selected day does not render one day earlier.
      function utcIsoToLocalMidnight(iso){
        const utc = new Date(iso);
        // Shift forward by timezone offset to land on local midnight of the chosen calendar date.
        return new Date(utc.getTime() + utc.getTimezoneOffset()*60000);
      }
      const startLocal = utcIsoToLocalMidnight(ds);
      const endLocal = utcIsoToLocalMidnight(de);
      const assignment = { id: created?.id ?? Math.random(), ticketId, projectId, start: startLocal, end: endLocal, resourceName: selectedUser?.name||selectedUser?.fullName||selectedUser?.identifier||`User ${selectedUser?.id}`, resourceIdentifier: selectedUser?.id??null, ticketSummary: getTicketSummary((tickets||[]).find(t => extractTicketId(t)===ticketId))||null, raw: created||payload };
      // Ensure ticket is in context so filteredAssignments doesn't filter it out via validTicketIds check
      const ticketInContext = (tickets||[]).some(t => { const tid = t?.id ?? t?.ticketId ?? t?.objectId; return tid != null && String(tid) === String(ticketId); });
      if (!ticketInContext) {
        const ticketObj = (projectTickets||[]).find(t => String(t?.id ?? t?.ticketId ?? t?.objectId) === String(ticketId));
        setTickets(prev => [...(prev||[]), ticketObj ?? { id: ticketId, ticketId }]);
      }
      setAssignments(prev => [...prev, assignment]);
      onClose?.();
    } catch(e){
      // Capture and surface specific backend error: "Member not assigned to project."
      if (e && typeof e === 'object') {
        const bodyText = e.body || e.message || '';
        const normalized = String(bodyText).toLowerCase();
        if (e.status === 500 && normalized.includes('member not assigned to project')) {
          setError(new Error('Member not assigned to project.'));
          setRolePickerVisible(true);
        } else {
          setError(e);
        }
      } else {
        setError(e);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <Modal>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <h3 style={{margin:0}}>Add Scheduled Resource</h3>
        <button onClick={() => onClose?.()} disabled={submitting}>Close</button>
      </div>
      <div style={{marginTop:12,display:'grid',gap:12,color:'var(--white_coordinating, #ffffff)'}}>
        {/* Show top-level error unless it's the specific 'member not assigned' case (handled inline near actions) */}
        {error && !isMemberNotAssignedErr(error) && (
          <div style={{color:'#b00020'}}>Error: {error.message}</div>
        )}
        <div>
          <div style={{fontWeight:600,marginBottom:4}}>Project</div>
          <div style={{display:'flex', gap:8}}>
            <input
              placeholder="Search project id or name"
              value={searchProject}
              onChange={e=>setSearchProject(e.target.value)}
              onKeyDown={(e)=>{ if (e.key === 'Enter') { e.preventDefault(); applyProjectSearch(); } }}
              style={{flex:1,padding:'6px 8px',background:'#ffffff',color:'#000',border:'1px solid #ccc',borderRadius:4}}
            />
            <button onClick={applyProjectSearch} style={{background:'#ffffff',color:'#000',border:'1px solid #ccc',borderRadius:4,padding:'6px 10px'}}>Search</button>
          </div>
          <div style={{marginTop:6, fontSize:12, color:'#ddd'}}>Press Enter or click Search to select by ID.</div>
          <div style={{maxHeight:160,overflow:'auto',border:'1px solid #ccc',background:'#fff',color:'#000'}}>
            {filteredProjects.slice(0,200).map(p=>{ const id = extractProjectId(p); const label = `${id} • ${extractProjectName(p)}`; const checked = String(selectedProjectId)===String(id); return (
              <label key={id} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 8px'}}>
                <input type="radio" name="proj" checked={checked} onChange={()=>{ setSelectedProjectId(id); setSelectedTicketId(null); }} />
                <span style={{color:'#000'}}>{label}</span>
              </label>
            );})}
          </div>
        </div>
        <div style={{opacity:selectedProjectId?1:0.4,pointerEvents:selectedProjectId?'auto':'none',transition:'opacity 160ms'}}>
          <div style={{fontWeight:600,marginBottom:4}}>Ticket</div>
          <SearchableSelect
            disabled={!selectedProjectId}
            items={ticketsForProject.slice(0, 1000).map((t) => ({
              value: extractTicketId(t),
              label: `${extractTicketId(t)} • ${getTicketSummary(t) || ''}`,
            }))}
            value={selectedTicketId}
            onChange={(val) => setSelectedTicketId(val)}
            placeholder="Search tickets by id or summary"
          />
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Or enter Ticket ID (e.g. 12345)"
              value={manualTicketId}
              onChange={(e) => setManualTicketId(e.target.value)}
              disabled={!selectedProjectId}
              style={{ flex: 1, minWidth: 220, padding: '6px 8px', background:'#ffffff', color:'#000', border:'1px solid #ccc', borderRadius:4 }}
            />
            <button
              type="button"
              onClick={() => {
                const v = String(manualTicketId || '').trim();
                if (!v) return;
                setSelectedTicketId(/^\d+$/.test(v) ? Number(v) : v);
              }}
              disabled={!selectedProjectId || !String(manualTicketId || '').trim()}
              style={{ background:'#ffffff', color:'#000', border:'1px solid #ccc', borderRadius:4, padding:'6px 10px' }}
            >
              Use ID
            </button>
          </div>
          {selectedProjectId && backgroundLoading && (
            <div style={{ marginTop: 6, fontSize: 12, color: '#ddd' }}>
              Ticket details are still loading in the background — you can use the manual Ticket ID field to add a resource now.
            </div>
          )}
          {selectedProjectId && (
            <div style={{marginTop:6, fontSize:12, color:'#ddd'}}>
              {loadingProjectTickets ? (
                <span>Fetching tickets for project {String(selectedProjectId)}…</span>
              ) : projectTicketsError ? (
                <span style={{ color:'#ffb3b3' }}>Failed to fetch project tickets</span>
              ) : (
                <span>
                  Showing {ticketsForProject.length} tickets for project {String(selectedProjectId)}
                  {projectTickets && projectTickets.length ? ` • ${projectTickets.length} loaded from API` : ''}
                </span>
              )}
            </div>
          )}
        </div>
        <div style={{opacity:selectedTicketId?1:0.4,pointerEvents:selectedTicketId?'auto':'none',transition:'opacity 160ms'}}>
          <div style={{fontWeight:600,marginBottom:4}}>User</div>
          <input placeholder="Search user id or name" value={searchUser} onChange={e=>setSearchUser(e.target.value)} style={{width:'100%',padding:'6px 8px',marginBottom:6,background:'#ffffff',color:'#000',border:'1px solid #ccc',borderRadius:4}} />
          <div style={{maxHeight:160,overflow:'auto',border:'1px solid #ccc',background:'#fff',color:'#000'}}>
            {loadingUsers && <div style={{padding:8}}>Loading users…</div>}
            {!loadingUsers && filteredUsers.slice(0,300).map(u=>{ const id = u?.id; const name = u?.name||u?.fullName||u?.identifier||`User ${id}`; const label = `${id??''} ${id?'• ':''}${name}`; const checked = selectedUser?.id===id; return (
              <label key={id??name} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 8px'}}>
                <input type="radio" name="user" checked={checked} onChange={()=>setSelectedUser(u)} />
                <span style={{color:'#000'}}>{label}</span>
              </label>
            );})}
          </div>
        </div>
        <div style={{display:'flex',gap:12,alignItems:'center',opacity:selectedUser?1:0.4,pointerEvents:selectedUser?'auto':'none',transition:'opacity 160ms'}}>
          <div>
            <div style={{fontWeight:600,marginBottom:4}}>Start date</div>
            <input type="date" value={dateStart} onChange={e=>setDateStart(e.target.value)} style={{background:'#ffffff',color:'#000',border:'1px solid #ccc',borderRadius:4,padding:'4px 6px'}} />
          </div>
          <div>
            <div style={{fontWeight:600,marginBottom:4}}>End date</div>
            <input type="date" value={dateEnd} onChange={e=>setDateEnd(e.target.value)} style={{background:'#ffffff',color:'#000',border:'1px solid #ccc',borderRadius:4,padding:'4px 6px'}} />
          </div>
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={onSubmit} disabled={submitting||!selectedProjectId||!selectedTicketId||!selectedUser||!dateStart||!dateEnd} style={{background:'#ffffff',color:'#000',border:'1px solid #ccc',borderRadius:4,padding:'6px 10px'}}>{submitting?'Creating…':'Create'}</button>
            {error && isMemberNotAssignedErr(error) && (
              <span style={{ color: '#b00020' }}>Member not assigned to project.</span>
            )}
          </div>
          {rolePickerVisible && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Assign user to project with role</div>
              <SearchableSelect
                items={(projectRoles || []).map(r => ({ value: r?.id ?? r?.identifier ?? r?.value, label: r?.name || r?.roleName || String(r?.id) }))}
                value={selectedProjectRoleId}
                onChange={val => setSelectedProjectRoleId(val)}
                placeholder="Search project roles…"
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <button
                  onClick={async () => {
                    try {
                      if (!selectedProjectId || !selectedUser || !selectedProjectRoleId) return;
                      setAddingToProject(true);
                      const payload = {
                        member: { id: selectedUser.id },
                        projectRole: { id: selectedProjectRoleId },
                      };
                      await postAddMemberToProject(selectedProjectId, payload, token);
                      setError(null);
                      setRolePickerVisible(false);
                    } catch (e) {
                      setError(e);
                    } finally {
                      setAddingToProject(false);
                    }
                  }}
                  disabled={addingToProject || !selectedProjectRoleId || !selectedUser || !selectedProjectId}
                  style={{ background:'#ffffff', color:'#000', border:'1px solid #ccc', borderRadius:4, padding:'6px 10px' }}
                >
                  {addingToProject ? 'Adding…' : 'Add to Project'}
                </button>
                {error && !isMemberNotAssignedErr(error) && (
                  <span style={{ color:'#b00020' }}>{String(error.message)}</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
